import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type IRowNode } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
  gridApi: GridApi | null,
  workStationId: string
}

const defState = {
  gridApi: null,
  workStationId: ""
};

interface SearchParams {
  regDateFrom: string,
  regDateTo: string,
  workStationId: string,
  skuType: string,
  skuSize: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  regDateFrom: dayjs().format("YYYYMMDD") + "0000",
  regDateTo: dayjs().format("YYYYMMDD") + "2359",
  workStationId: "",
  skuType: "",
  skuSize: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 SKU를 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "출고작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

function ReInboundLog(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업일시", field: "regDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "작업대", field: "workStationId", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "Location", field: "locationId", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU No", field: "skuNoEx", width: 150, cellStyle: {textAlign: "center"} },
    { headerName: "SKU Type", field: "skuType", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuType"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Size", field: "skuSize", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuSize"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Cell", field: "skuCellQty", width: 120, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        return (
          (n.data.outPossYn == "Y")
        );
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab]);
  // 끝: 바코드 스캔 기능 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/log/reInbound_log.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/log/reInbound_log", searchParams);
  }

  async function resetParams() {
    router.navigateByPath("/log/reInbound_log", {});
  }
 
  async function reqSkuPick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("지정출고 SKU를 선택해 주세요.");
      return;
    }

    if (!state.workStationId) {
      kutil.alert("출고작업대를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      info.workStationId = state.workStationId;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/log/reInbound_log.proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list-grid-top">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>미작업 재입고 로그</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.write &&
            <Button variant="outlined" onClick={reqSkuPick}>지정출고</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-time-range">
            <p>조회일시</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.regDateFrom)}
                  onChange={(e) => doSearch(e, { regDateFrom: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  maxDateTime={dayjs(searchParams.regDateTo)}
                />
                <span>-</span>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.regDateTo)}
                  onChange={(e) => doSearch(e, { regDateTo: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  minDateTime={dayjs(searchParams.regDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>작업대</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.workStationId}
                onChange={(e) => {doSearch(e, {workStationId: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.workStationId as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU Type</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuType}
                onChange={(e) => {doSearch(e, {skuType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU Size</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuSize}
                onChange={(e) => {doSearch(e, {skuSize: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuSize as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          <div className="workstation">
            <p>출고작업대</p>
            <FormControl size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
              {(commonCode.workStationId as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* 로그 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 로그 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(ReInboundLog);
